import { getPermission } from '@auth';
import { useAuth0 } from '@auth0/auth0-react';
import { UIPaths } from '@consts';
import { useGoalsService } from '@hooks/useGoalsService';
import { useGoalSubscriptionService } from '@hooks/useGoalSubscriptionService';
import { usePaymentsService } from '@hooks/usePaymentsService';
import { PermissionEntityType, PermissionType } from '@models/authorization';
import { ToastType } from '@models/common';
import { GoalModel, GoalSellingStatus } from '@models/goals';
import { GoalSubscriptionPriority, GoalSubscriptionStatus } from '@models/goalSubscriptions';
import { PaymentProvider, PaymentSessionReason, ProductType } from '@models/payments';
import * as Sentry from '@sentry/nextjs';
import { getStoredReferralCode, readReferralFromUrl } from '@services/referralStorage';
import { useStore } from '@store';
import { useRouter } from 'next/router';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';

export enum ActionType {
  START_SUBSCRIPTION = 'START_SUBSCRIPTION',
  SUBSCRIPTION_CREATED_ACTIVE = 'SUBSCRIPTION_CREATED_ACTIVE',
  SUBSCRIPTION_CREATED_PENDING = 'SUBSCRIPTION_CREATED_PENDING',
  SUBSCRIPTION_ACTIVATED = 'SUBSCRIPTION_ACTIVATED',
  SUBSCRIPTION_FAILED = 'SUBSCRIPTION_FAILED',
  REPLACE_GOAL = 'REPLACE_GOAL',
  RESET_TRANSIENT_STATE = 'RESET_TRANSIENT_STATE'
}

export enum Status {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  PENDING_ACTIVATION = 'PENDING_ACTIVATION',
  SUBSCRIBED = 'SUBSCRIBED',
  ERROR = 'ERROR'
}

type Action =
  | { type: ActionType.START_SUBSCRIPTION }
  | { type: ActionType.SUBSCRIPTION_CREATED_ACTIVE; payload: { id: number } }
  | { type: ActionType.SUBSCRIPTION_CREATED_PENDING; payload: { id: number } }
  | { type: ActionType.SUBSCRIPTION_ACTIVATED }
  | { type: ActionType.SUBSCRIPTION_FAILED }
  | { type: ActionType.REPLACE_GOAL; payload: { goal: GoalModel } }
  | { type: ActionType.RESET_TRANSIENT_STATE };

interface State {
  goal: GoalModel | null;
  status: Status;
  subscriptionId: number | null;
}

const initialState: State = {
  goal: null,
  status: Status.IDLE,
  subscriptionId: null
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionType.START_SUBSCRIPTION:
      return { ...state, status: Status.LOADING };
    case ActionType.SUBSCRIPTION_CREATED_ACTIVE:
      return { ...state, status: Status.SUBSCRIBED, subscriptionId: action.payload.id };
    case ActionType.SUBSCRIPTION_CREATED_PENDING:
      return { ...state, status: Status.PENDING_ACTIVATION, subscriptionId: action.payload.id };
    case ActionType.SUBSCRIPTION_ACTIVATED:
      return { ...state, status: Status.SUBSCRIBED };
    case ActionType.SUBSCRIPTION_FAILED:
      return { ...state, status: Status.ERROR };
    case ActionType.REPLACE_GOAL: {
      const newGoal = action.payload.goal;
      const isNewEntity = newGoal.entityId !== state.goal?.entityId;
      if (isNewEntity) {
        return { goal: newGoal, status: Status.IDLE, subscriptionId: null };
      }
      return { ...state, goal: newGoal };
    }
    case ActionType.RESET_TRANSIENT_STATE:
      return { ...state, status: Status.IDLE, subscriptionId: null };
    default:
      return state;
  }
}

interface PaymentAndOrderContextType {
  state: State;
  isSubscribed: boolean;
  isFree: boolean;
  isSoldOut: boolean;
  canEnroll: boolean;
  isLoading: boolean;
  isAuthor: boolean;
  handleMainAction: (event?: React.MouseEvent, paymentProvider?: PaymentProvider) => Promise<void>;
}

const PaymentAndOrderContext = createContext<PaymentAndOrderContextType | undefined>(undefined);

export function PaymentAndOrderProvider({
  goal,
  children
}: {
  goal: GoalModel;
  children: React.ReactNode;
}) {
  const router = useRouter();
  const { t } = useTranslation();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { createGoalSubscription, getGoalSubscription } = useGoalSubscriptionService();
  const { getGoalLandingPage } = useGoalsService();
  const { createPaymentSession } = usePaymentsService();
  const setToast = useStore((s) => s.setToast);
  const [state, dispatch] = useReducer(reducer, { ...initialState, goal });

  const refreshGoal = useCallback(async () => {
    if (!goal?.entityId) return;
    try {
      const fresh = await getGoalLandingPage(goal.entityId);
      if (fresh) dispatch({ type: ActionType.REPLACE_GOAL, payload: { goal: fresh } });
    } catch (error) {
      Sentry.captureException(error, {
        extra: { goalEntityId: goal?.entityId, context: 'refresh goal after payment outcome' }
      });
    }
  }, [getGoalLandingPage, goal?.entityId]);

  const isLoading = state.status === Status.LOADING || state.status === Status.PENDING_ACTIVATION;
  const isSubscribed =
    state.status === Status.SUBSCRIBED ||
    state.goal?.subscription?.status === GoalSubscriptionStatus.ACTIVE;
  const isSoldOut = state.goal?.sellingStatus === GoalSellingStatus.SOLD_OUT;
  const isFree = state.goal?.currentPrice?.amount === 0;
  const canEnroll = state.goal?.enrollSpec?.canEnroll;
  const isAuthor = useMemo(() => {
    return !!getPermission(
      state.goal?.__permissions || [],
      PermissionType.EDIT,
      PermissionEntityType.GOAL
    );
  }, [state.goal?.__permissions]);

  const prevGoalPropRef = useRef(goal);
  useEffect(() => {
    if (!goal) return;
    if (goal === prevGoalPropRef.current) return;
    dispatch({ type: ActionType.REPLACE_GOAL, payload: { goal } });
    prevGoalPropRef.current = goal;
  }, [goal]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (state.status === Status.PENDING_ACTIVATION && state.subscriptionId) {
      intervalId = setInterval(async () => {
        try {
          const subscription = await getGoalSubscription(state.subscriptionId!);
          if (subscription.status === GoalSubscriptionStatus.ACTIVE) {
            dispatch({ type: ActionType.SUBSCRIPTION_ACTIVATED });
          }
        } catch (error) {
          Sentry.captureException(error, {
            extra: { subscriptionId: state.subscriptionId, context: 'polling subscription status' }
          });
        }
      }, 2000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [state.status, state.subscriptionId, getGoalSubscription]);

  const handleMainAction = useCallback(
    async (event?: React.MouseEvent, paymentProvider: PaymentProvider = PaymentProvider.STRIPE) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (state.status === Status.PENDING_ACTIVATION) {
        return;
      }

      if (isSubscribed) {
        const subscriptionId =
          state.subscriptionId ?? state.goal?.subscription?.id ?? goal.subscription?.id;
        if (subscriptionId) {
          router.push(UIPaths.assignments(goal.entityId, subscriptionId));
          return;
        }
      }

      if (isFree || canEnroll) {
        if (isAuthenticated) {
          try {
            dispatch({ type: ActionType.START_SUBSCRIPTION });

            // A free goal has no Stripe checkout, but the purchase must still be
            // recorded server-side (entitlement, "My subscriptions", revenue).
            // Hit /payments/sessions first: for a free goal it returns
            // PRODUCT_IS_FREE and creates the order (idempotent). Gated on isFree
            // only — a canEnroll-but-paid goal (gotoit) would open a Stripe session.
            if (isFree) {
              await createPaymentSession({
                product: {
                  type: ProductType.GOAL,
                  data: { goalEntityId: goal.entityId }
                },
                referralCode: getStoredReferralCode() ?? readReferralFromUrl() ?? undefined
              });
            }

            const result = await createGoalSubscription({
              goalEntityId: goal.entityId,
              priority: GoalSubscriptionPriority.MEDIUM
            });

            if (result.success) {
              if (result.status === GoalSubscriptionStatus.ACTIVE) {
                dispatch({
                  type: ActionType.SUBSCRIPTION_CREATED_ACTIVE,
                  payload: { id: result.id! }
                });
              } else if (result.status === GoalSubscriptionStatus.PENDING_ACTIVE) {
                dispatch({
                  type: ActionType.SUBSCRIPTION_CREATED_PENDING,
                  payload: { id: result.id! }
                });
              } else {
                dispatch({ type: ActionType.SUBSCRIPTION_FAILED });
                Sentry.captureMessage('Subscription creation failed with unexpected status', {
                  extra: { status: result.status, goalEntityId: goal?.entityId }
                });
              }
            } else {
              dispatch({ type: ActionType.SUBSCRIPTION_FAILED });
              Sentry.captureMessage('Subscription creation failed', {
                extra: { goalEntityId: goal?.entityId }
              });
            }
          } catch (error) {
            Sentry.captureException(error, {
              extra: { goalEntityId: goal?.entityId, context: 'create subscription' }
            });
            dispatch({ type: ActionType.SUBSCRIPTION_FAILED });
          }
          return;
        } else {
          const base = window.location.pathname + window.location.search;
          const h = window.location.hash;
          const returnUrl =
            h === '#pricing-cta' || h === '#pricing' ? `${base}${h}` : `${base}#pricing-cta`;
          loginWithRedirect({
            appState: { returnTo: returnUrl }
          });
          return;
        }
      }

      if (goal.externalSaleUrl) {
        window.location.href = goal.externalSaleUrl;
        return;
      } else {
        try {
          dispatch({ type: ActionType.START_SUBSCRIPTION });
          const session = await createPaymentSession({
            product: {
              type: ProductType.GOAL,
              data: { goalEntityId: goal.entityId }
            },
            referralCode: getStoredReferralCode() ?? readReferralFromUrl() ?? undefined,
            paymentProvider
          });

          if (
            session.reason === PaymentSessionReason.SESSION_CREATED &&
            session.sessionRedirectUrl
          ) {
            window.location.href = session.sessionRedirectUrl;
          } else if (session.reason === PaymentSessionReason.PRODUCT_ALREADY_ACQUIRED) {
            dispatch({ type: ActionType.RESET_TRANSIENT_STATE });
            setToast({
              message: t('toasts.goalAlreadyEnrolled'),
              type: ToastType.INFO
            });
            await refreshGoal();
          } else if (session.reason === PaymentSessionReason.PRODUCT_IS_FREE) {
            dispatch({ type: ActionType.RESET_TRANSIENT_STATE });
            await refreshGoal();
          } else {
            dispatch({ type: ActionType.SUBSCRIPTION_FAILED });
            Sentry.captureMessage('Unexpected payment session response', {
              extra: { goalEntityId: goal?.entityId, session }
            });
          }
        } catch (error) {
          Sentry.captureException(error, {
            extra: { goalEntityId: goal?.entityId, context: 'create payment session' }
          });
          dispatch({ type: ActionType.SUBSCRIPTION_FAILED });
        }
      }
    },
    [
      isSubscribed,
      isSoldOut,
      isFree,
      canEnroll,
      state.subscriptionId,
      state.goal?.subscription?.id,
      state.goal?.entityId,
      state.status,
      goal,
      isAuthenticated,
      router,
      createGoalSubscription,
      createPaymentSession,
      loginWithRedirect,
      refreshGoal,
      setToast,
      t
    ]
  );

  const value = useMemo(
    () => ({
      state,
      isSubscribed,
      isFree,
      isSoldOut,
      canEnroll,
      isLoading,
      isAuthor,
      handleMainAction
    }),
    [state, isSubscribed, isFree, isSoldOut, canEnroll, isLoading, isAuthor, handleMainAction]
  );

  return (
    <PaymentAndOrderContext.Provider value={value}>{children}</PaymentAndOrderContext.Provider>
  );
}

export function usePaymentAndOrder() {
  const context = useContext(PaymentAndOrderContext);
  if (context === undefined) {
    throw new Error('usePaymentAndOrder must be used within a PaymentAndOrderProvider');
  }
  return context;
}

// Non-throwing variant: returns undefined outside a PaymentAndOrderProvider.
// Used by components (e.g. the shared pricing section) that can run either inside
// the goal provider or standalone (driven entirely by props, as on the package page).
export function usePaymentAndOrderOptional() {
  return useContext(PaymentAndOrderContext);
}
